import { Button, Layout, Menu, Tooltip, theme } from 'antd';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CONFERENCE_WORKSPACE_PARENT_PATH } from './conferenceWorkspaceConfig';
import { getMenuSelectedKeys } from './workspaceMenuUtils';

const { Sider } = Layout;

const innerStyle = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
};

const logoBaseStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    height: 56,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    transition: 'padding .2s',
    flexShrink: 0,
};

const logoImgStyle = { width: 24, height: 24, flexShrink: 0 };

const logoTextBaseStyle = {
    fontWeight: 700,
    fontSize: 18,
};

const backBarStyle = {
    flexShrink: 0,
    padding: '8px 12px',
};

const menuScrollStyle = {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
};

const menuStyle = { borderRight: 'none', fontSize: 16 };

/**
 * 会议项目工作空间侧栏：与医项目 WorkspaceSider 形态一致，仅菜单数据与返回路径不同。
 *
 * @param {{ collapsed: boolean, menuEntries: { key: string, label: string, disabled?: boolean, icon?: string }[] }} props
 */
export default function ConferenceSider({ collapsed, menuEntries }) {
    const { token } = theme.useToken();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const logoStyle = useMemo(
        () => ({
            ...logoBaseStyle,
            justifyContent: 'center',
            borderBottom: `1px solid ${token.colorSplit}`,
        }),
        [token.colorSplit],
    );

    const logoTextStyle = useMemo(
        () => ({
            ...logoTextBaseStyle,
            color: token.colorTextHeading,
        }),
        [token.colorTextHeading],
    );

    const siderStyle = useMemo(
        () => ({
            background: token.colorBgContainer,
            borderRight: `1px solid ${token.colorSplit}`,
            position: 'sticky',
            top: 0,
            height: '100vh',
        }),
        [token.colorBgContainer, token.colorSplit],
    );

    const backBtnStyle = useMemo(
        () => ({
            color: token.colorText,
            width: '100%',
            textAlign: 'left',
            paddingInline: collapsed ? 0 : undefined,
            justifyContent: collapsed ? 'center' : 'flex-start',
        }),
        [collapsed, token.colorText],
    );

    const menuItems = useMemo(
        () =>
            menuEntries.map(row => ({
                key: row.key,
                label: row.label,
                disabled: row.disabled,
                icon: row.icon ? <i className={row.icon} aria-hidden="true" /> : undefined,
            })),
        [menuEntries],
    );

    const selectedKeys = useMemo(() => getMenuSelectedKeys(pathname, menuEntries), [menuEntries, pathname]);

    const handleMenuClick = useCallback(({ key }) => navigate(key), [navigate]);

    const backButton = (
        <Button type="text" size="small" icon={<i className="bi bi-arrow-left-circle-fill" aria-hidden style={{ color: token.colorTextTertiary, opacity: 0.75 }} />} onClick={() => navigate(CONFERENCE_WORKSPACE_PARENT_PATH)} style={backBtnStyle} aria-label="返回会议项目列表">
            {!collapsed ? '返回会议项目列表' : null}
        </Button>
    );

    return (
        <Sider id="conference-sider" collapsed={collapsed} collapsedWidth={48} width={200} style={siderStyle}>
            <div style={innerStyle}>
                <div id="conference-sider-brand" style={logoStyle}>
                    <img className="logo" src="/logo.svg" alt="logo" style={logoImgStyle} />
                    {!collapsed && (
                        <span className="brandTitle" style={logoTextStyle}>
                            华媒康讯•智管平台
                        </span>
                    )}
                </div>

                <div style={{ ...backBarStyle, borderBottom: `1px solid ${token.colorSplit}` }}>{collapsed ? <Tooltip title="返回会议项目列表">{backButton}</Tooltip> : backButton}</div>

                <div style={menuScrollStyle}>
                    <Menu theme="light" mode="inline" inlineCollapsed={collapsed} selectedKeys={selectedKeys} items={menuItems} onClick={handleMenuClick} style={menuStyle} getPopupContainer={() => document.body} />
                </div>
            </div>
        </Sider>
    );
}
